/**
 * Generic add/edit form modal for plain field-list resources (Vendor, Worker).
 *
 * Extracted from the duplicated Vendor & Worker modals: same shell, footer, submitting
 * state, error line, and input styling — only the FIELDS, validation, and submit differ,
 * so those are config. NOT used by SalaryModal (that's a read-display + effective-dated
 * hybrid, intentionally bespoke — see SalaryModal.tsx).
 *
 * Fields are controlled by the parent via `values` + `onChange` (the parent owns state so it
 * can seed edit-mode defaults and apply cross-field rules); this component only renders them.
 */
import React from 'react';

export type FieldType = 'text' | 'number' | 'date' | 'select';

export interface FieldDef {
	name: string;
	label: string;
	type: FieldType;
	required?: boolean;
	placeholder?: string;
	disabled?: boolean;
	/** For select fields. */
	options?: { value: string | number; label: string }[];
	/** Inline help/validation text shown under the field. */
	hint?: string;
	/** Render this field at half width (pairs with the next half-width field on a row). */
	half?: boolean;
}

interface FormModalProps {
	open: boolean;
	title: string;
	fields: FieldDef[];
	values: Record<string, string | number>;
	onChange: (name: string, value: string | number) => void;
	canSubmit: boolean;
	submitting: boolean;
	submitLabel: string;
	error?: string | null;
	onSubmit: () => void;
	onClose: () => void;
}

const inputCls =
	'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-500';

export const FormModal: React.FC<FormModalProps> = ({
	open,
	title,
	fields,
	values,
	onChange,
	canSubmit,
	submitting,
	submitLabel,
	error,
	onSubmit,
	onClose,
}) => {
	if (!open) return null;

	const renderField = (f: FieldDef) => {
		const v = values[f.name] ?? '';
		const label = (
			<label className='mb-1 block text-xs font-medium text-gray-700'>
				{f.label} {f.required && <span className='text-red-500'>*</span>}
			</label>
		);
		const control =
			f.type === 'select' ? (
				<select
					value={v}
					disabled={f.disabled}
					onChange={e => onChange(f.name, e.target.value === '' ? '' : isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value))}
					className={inputCls}
				>
					<option value=''>{f.placeholder ?? 'Select…'}</option>
					{f.options?.map(o => (
						<option key={o.value} value={o.value}>
							{o.label}
						</option>
					))}
				</select>
			) : (
				<input
					type={f.type}
					value={v}
					disabled={f.disabled}
					placeholder={f.placeholder}
					onChange={e => onChange(f.name, e.target.value)}
					className={inputCls}
				/>
			);
		return (
			<div key={f.name} className={f.half ? 'flex-1' : ''}>
				{label}
				{control}
				{f.hint && <p className='mt-1 text-[11px] text-red-600'>{f.hint}</p>}
			</div>
		);
	};

	// Group consecutive half-width fields into rows.
	const rows: FieldDef[][] = [];
	for (let i = 0; i < fields.length; i++) {
		if (fields[i].half && fields[i + 1]?.half) {
			rows.push([fields[i], fields[i + 1]]);
			i++;
		} else {
			rows.push([fields[i]]);
		}
	}

	return (
		<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'>
			<div className='w-full max-w-md rounded-lg bg-white shadow-xl'>
				<div className='border-b border-gray-200 px-5 py-4'>
					<h3 className='text-base font-semibold text-gray-900'>{title}</h3>
				</div>

				<div className='space-y-4 px-5 py-4'>
					{rows.map((row, i) =>
						row.length === 2 ? (
							<div key={i} className='flex gap-3'>
								{row.map(renderField)}
							</div>
						) : (
							renderField(row[0])
						)
					)}
					{error && <p className='text-[11px] text-red-600'>{error}</p>}
				</div>

				<div className='flex justify-end gap-2 border-t border-gray-200 px-5 py-3'>
					<button
						type='button'
						onClick={onClose}
						disabled={submitting}
						className='rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50'
					>
						Cancel
					</button>
					<button
						type='button'
						onClick={onSubmit}
						disabled={!canSubmit || submitting}
						className='rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50'
					>
						{submitting ? 'Saving…' : submitLabel}
					</button>
				</div>
			</div>
		</div>
	);
};

export default FormModal;
